import { lazy, useEffect, useState, Suspense, useMemo } from 'react';
import { Provider } from '@vgtv/api-client';

import {
  getAssetLabels,
  getAssetShareUrl,
  getAssetTags,
  getAuthors,
  willAssetHaveDisplayAd,
} from '../../shared/helpers/assetHelpers';
import { useViewportType } from '../utils/useViewportType';
import { AdsContainer } from '../AdsContainer/AdsContainer';
import { AssetDetails } from '../AssetDetails/AssetDetails';
import { config } from '../../config';
import { getAssetProps } from '../../helpers/getAssetProps';
import { AssetContainer } from '../AssetContainer/AssetContainer';
import { observeViewportResize } from '../../utils/viewport/observeViewportResize';
import { playerContainer } from '../../player/playerContainer';
import {
  LIVERPOOL_FC_ASSET_ID,
  LiverpoolTimetable,
} from '../LiverpoolTimetable/LiverpoolTimetable';
import { Player } from '../Player/Player';
import { AssetMetadata } from '../AssetMetadata/AssetMetadata';

import type {
  SvpAsset,
  SvpAssetAdditional,
} from '@vgtv/api-client/lib/svp_asset';
import type { FC } from 'react';

import * as styles from './PlayerWithAssetDetails.module.scss';

const NewsTimetable = lazy(() =>
  import('../NewsTimetable/NewsTimetable').then(({ NewsTimetable }) => ({
    default: NewsTimetable,
  }))
);

export interface PlayerWithAssetDetailsProps {
  asset: SvpAssetAdditional;
  startTime?: number;
}

const VGTV_NEWS_ASSET_ID = 149692 as SvpAsset['id'];

const PlayerWithAssetDetails: FC<PlayerWithAssetDetailsProps> = ({
  asset,
  startTime,
}) => {
  const { isMobile } = useViewportType();

  const [timetableMaxHeight, setTimetableMaxHeight] = useState<number>();

  useEffect(() => {
    setTimetableMaxHeight(playerContainer.getDimensions().height);

    const { stopObserving } = observeViewportResize(() => {
      setTimetableMaxHeight(playerContainer.getDimensions().height);
    });

    return () => stopObserving();
  }, []);

  const { category, description, additional } = asset;

  const { chapters } = additional;
  const tags = useMemo(() => getAssetTags(asset), [asset]);

  return (
    <AssetContainer asset={asset} className={styles.container}>
      {config.provider === Provider.VGTV &&
        asset.provider === 'vgtv' &&
        !isMobile && (
          <AdsContainer
            renderCompanionAd
            assetId={asset.id}
            categoryId={category.id}
            onShow={playerContainer.positionOverNode} // move the player below the ad if one is shown / hidden
            onLoad={playerContainer.positionOverNode} // adjust the player position if ad is initially shown
            className={styles.desktopAd}
            key={`desktop-${asset.id}`} // force re-render on asset change
            initiallyShown={willAssetHaveDisplayAd(asset)}
          />
        )}
      <Player asset={asset} startTime={startTime} />
      {config.provider === Provider.AB && (
        <AdsContainer
          renderCompanionAd
          categoryId={category.id}
          assetId={asset.id}
          initiallyShown={willAssetHaveDisplayAd(asset)}
          key={`desktop-${asset.id}`} // force re-render on asset change
          className={styles.companionAdAB}
        />
      )}
      <AssetDetails
        tags={tags}
        chapters={chapters}
        shareUrl={getAssetShareUrl(asset)}
        {...getAssetProps(asset)}
        description={description}
        labels={getAssetLabels(asset)}
        authorIds={getAuthors(asset)}
        metadata={<AssetMetadata asset={asset} />}
      />
      {asset.id === VGTV_NEWS_ASSET_ID && (
        <Suspense fallback={null}>
          {isMobile ? (
            <NewsTimetable variant="list" className={styles.timetableList} />
          ) : (
            <div className={styles.timetableSidebarWrapper}>
              <NewsTimetable
                variant="sidebar"
                style={{
                  maxHeight: timetableMaxHeight,
                }}
              />
            </div>
          )}
        </Suspense>
      )}
      {asset.id === LIVERPOOL_FC_ASSET_ID &&
        (isMobile ? (
          <LiverpoolTimetable variant="list" className={styles.timetableList} />
        ) : (
          <div className={styles.timetableSidebarWrapper}>
            <LiverpoolTimetable
              variant="sidebar"
              style={{
                maxHeight: timetableMaxHeight,
              }}
            />
          </div>
        ))}
      {config.provider === Provider.VGTV &&
        asset.provider === 'vgtv' &&
        isMobile && (
          <AdsContainer
            renderCompanionAd
            assetId={asset.id}
            categoryId={category.id}
            initiallyShown={willAssetHaveDisplayAd(asset)}
            key={`mobile-${asset.id}`} // force re-render on asset change
          />
        )}
    </AssetContainer>
  );
};

export { PlayerWithAssetDetails };
